import type { QueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { apiClient } from '@/shared/api/apiClient'
import { PRODUCTS_QUERY_KEY } from '@/shared/hooks/api/product'
import { PRODUCT_SOURCES_KEY } from '@/shared/hooks/api/productSource'
import { normalizeFileName } from '@/shared/lib/normalize-file-name'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'
import { resolveProductSourceContentType } from '@/features/product-catalog/utils/productSourceFileTypes'
import {
  persistProductSourceUploadFailed,
  trackInFlightProductSourceUpload,
  untrackInFlightProductSourceUpload,
} from '@/features/product-catalog/lib/persistProductSourceUploadFailed'

async function invalidateProductSourceQueries(
  queryClient: QueryClient,
  productId: string
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [PRODUCT_SOURCES_KEY, productId] })
  await queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
}

export type UploadToExistingProductSourceArgs = {
  productId: string
  sourceId: string
  file: File
  queryClient: QueryClient
  translate: TFunction<'productCatalog'>
  /** When true, PATCH upload-status to PENDING before OSS (re-upload). */
  resetUploadStatusToPending?: boolean
  onFinished?: () => void
}

export async function uploadFileToExistingProductSource(
  args: UploadToExistingProductSourceArgs
): Promise<void> {
  const {
    productId,
    sourceId,
    file,
    queryClient,
    translate,
    resetUploadStatusToPending = false,
    onFinished,
  } = args

  const normalizedFilename = normalizeFileName(file.name)
  const filePathInBucket = `sources/${sourceId}/${normalizedFilename}`
  const contentType = resolveProductSourceContentType(file)

  trackInFlightProductSourceUpload(productId, sourceId)

  try {
    if (resetUploadStatusToPending) {
      await apiClient.patch(
        `/products/${productId}/sources/${sourceId}/upload-status`,
        { fileUploadStatus: 'PENDING' }
      )
    }

    const { data } = await apiClient.post<{ url: string }>(
      `/products/${productId}/sources/upload-url`,
      {
        key: file.name,
        sourceId,
        fileSizeBytes: file.size,
        contentType,
      }
    )

    await uploadFileToSignedOssUrl(data.url, file, {
      contentType,
      context: {
        feature: 'product-source',
        fileName: file.name,
        fileSizeBytes: file.size,
        contentType,
        productId,
        sourceId,
      },
    })

    const fileSizeKb = Math.ceil(file.size / 1024)
    await apiClient.patch(
      `/products/${productId}/sources/${sourceId}/file-path`,
      {
        filePathInBucket,
        fileSizeKb,
        name: file.name,
        type: contentType,
      }
    )

    await invalidateProductSourceQueries(queryClient, productId)
    toast.success(translate('sources.source.addSuccess'))
  } catch {
    await persistProductSourceUploadFailed(productId, sourceId)
    await invalidateProductSourceQueries(queryClient, productId)
    toast.error(translate('sources.source.addError'))
  } finally {
    untrackInFlightProductSourceUpload(sourceId)
    onFinished?.()
  }
}
